import {
	useDeleteVetterUserMutation,
	useGetMyVetterProfileQuery,
	useGetMyVetterTeamQuery,
	useInviteVetterUserMutation,
	useUpdateVetterEntityMutation,
	type GetMyVetterTeamQuery,
} from '@codezo/graphql';
import { MoreHorizontal, Pencil, UserPlus } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { StatusBadge, type StatusTone } from '../../components/ui/status-badge';
import { formatDate } from '../../lib/utils';
import { EditCompanyModal } from './EditCompanyModal';
import { EditProfileModal } from './EditProfileModal';
import { InviteMemberModal } from './InviteMemberModal';

type Member = GetMyVetterTeamQuery['getAllVetterUser']['data'][number];

/**
 * Active once they've signed in; otherwise whether they've been invited.
 * "Not invited" includes people who came in by a price-request link only.
 */
function memberStatus(m: Member): { label: string; tone: StatusTone } {
	if (m.authenticationUser?.lastActive) return { label: 'Active', tone: 'success' };
	if (m.invitedAt) return { label: `Invited ${formatDate(m.invitedAt)}`, tone: 'warn' };
	return { label: 'Not invited', tone: 'neutral' };
}

/**
 * The supplier's own company and people. Everything here is limited by the
 * backend to the caller's supplier; a supplier edits its name, description and
 * primary contact, invites and removes colleagues, and each person edits only
 * themselves. Link sessions never reach this page (see PortalGate).
 */
export function TeamPage() {
	const [inviteOpen, setInviteOpen] = useState(false);
	const [companyOpen, setCompanyOpen] = useState(false);
	const [profileOpen, setProfileOpen] = useState(false);
	const [removeTarget, setRemoveTarget] = useState<Member | null>(null);

	const { data: profileData, refetch: refetchProfile } = useGetMyVetterProfileQuery({
		fetchPolicy: 'no-cache',
	});
	const profile = profileData?.getMyVetterProfile;
	const company = profile?.vetterEntity;

	const { data, loading, refetch } = useGetMyVetterTeamQuery({
		variables: { input: { pagination: { currentPage: 1, limit: 100 } } },
		fetchPolicy: 'no-cache',
	});
	const members = data?.getAllVetterUser.data ?? [];

	const [invite] = useInviteVetterUserMutation();
	const [remove, { loading: removing }] = useDeleteVetterUserMutation();
	const [updateEntity] = useUpdateVetterEntityMutation();

	const me = members.find((m) => m.id === profile?.id) ?? null;
	const primaryName = members.find((m) => m.id === company?.primaryContactVetterUserId) ?? null;

	const handleResend = async (m: Member) => {
		try {
			const { data } = await invite({ variables: { id: m.id } });
			const res = data?.inviteVetterUser;
			if (!res?.success) throw new Error(res?.message || 'Could not send the invitation');
			toast.success(`Invitation sent to ${m.authenticationUser?.email ?? m.firstName}`);
			refetch();
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Could not send the invitation');
		}
	};

	const handleMakePrimary = async (m: Member) => {
		if (!company) return;
		try {
			const { data } = await updateEntity({
				variables: { id: company.id, input: { primaryContactVetterUserId: m.id } },
			});
			const res = data?.updateVetterEntity;
			if (!res?.success) throw new Error(res?.message || 'Could not change the contact');
			toast.success(`${m.firstName} is now the primary contact`);
			refetchProfile();
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Could not change the contact');
		}
	};

	const handleRemove = async () => {
		if (!removeTarget) return;
		try {
			const { data } = await remove({ variables: { id: removeTarget.id } });
			const res = data?.deleteVetterUser;
			if (!res?.success) throw new Error(res?.message || 'Could not remove them');
			toast.success(`${removeTarget.firstName} ${removeTarget.lastName} removed`);
			setRemoveTarget(null);
			refetch();
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Could not remove them');
		}
	};

	return (
		<div className="mx-auto w-full max-w-[var(--content-max)] px-4 pt-6 pb-20 sm:px-6 md:px-8 md:pt-8">
			<div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
				<div>
					<h1 className="mb-2 text-2xl leading-tight font-medium -tracking-[0.02em] text-text-1 sm:text-3xl">
						Team
					</h1>
					<p className="text-base text-text-3">
						Your company and the people who can see its jobs.
					</p>
				</div>
				{company && (
					<Button variant="outline" size="sm" onClick={() => setInviteOpen(true)}>
						<UserPlus size={14} data-icon="inline-start" />
						Invite a colleague
					</Button>
				)}
			</div>

			{/* ── Company ── */}
			{company && (
				<div className="mb-6 flex items-start gap-4 rounded-[var(--r-3)] border border-line-1 bg-surface px-5 py-4">
					<div className="min-w-0 flex-1">
						<div className="text-xs font-medium tracking-[0.14em] text-text-3 uppercase">
							Company
						</div>
						<div className="mt-1 text-lg font-medium text-text-1">{company.name}</div>
						<p className="mt-1 text-sm whitespace-pre-line text-text-3">
							{company.description || 'No description yet.'}
						</p>
						<div className="mt-2 text-sm text-text-3">
							Primary contact:{' '}
							<span className="text-text-1">
								{primaryName
									? `${primaryName.firstName} ${primaryName.lastName}`
									: 'Not set'}
							</span>
						</div>
					</div>
					<Button variant="ghost" size="sm" onClick={() => setCompanyOpen(true)}>
						<Pencil size={13} data-icon="inline-start" />
						Edit
					</Button>
				</div>
			)}

			{/* ── People ── */}
			<div className="overflow-hidden rounded-[var(--r-3)] border border-line-1 bg-surface">
				{loading && members.length === 0 ? (
					<div className="px-5 py-6 text-sm text-text-4">Loading…</div>
				) : (
					<div className="flex flex-col divide-y divide-line-1">
						{members.map((m) => {
							const isMe = m.id === profile?.id;
							const status = memberStatus(m);
							const isPrimary = m.id === company?.primaryContactVetterUserId;
							const active = !!m.authenticationUser?.lastActive;
							return (
								<div key={m.id} className="flex items-center gap-3 px-5 py-3">
									<Initials name={`${m.firstName} ${m.lastName}`} />
									<div className="min-w-0 flex-1">
										<div className="flex items-center gap-2 text-base text-text-1">
											<span className="truncate">
												{m.firstName} {m.lastName}
											</span>
											{isMe && (
												<span className="text-xs text-text-4">You</span>
											)}
											{isPrimary && (
												<span className="rounded-[var(--r-1)] bg-surface-fill px-1.5 text-2xs text-text-3">
													Primary contact
												</span>
											)}
										</div>
										<div className="truncate text-sm text-text-3">
											{m.authenticationUser?.email ?? '—'}
											{m.phone && ` · ${m.phone}`}
										</div>
									</div>
									<StatusBadge
										active
										activeLabel={status.label}
										activeTone={status.tone}
									/>
									<DropdownMenu>
										<DropdownMenuTrigger
											aria-label={`Actions for ${m.firstName}`}
											className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-md text-text-3 transition-colors hover:bg-surface-hover hover:text-text-1"
										>
											<MoreHorizontal size={14} />
										</DropdownMenuTrigger>
										<DropdownMenuContent align="end">
											{isMe && (
												<DropdownMenuItem
													onClick={() => setProfileOpen(true)}
												>
													Edit your details
												</DropdownMenuItem>
											)}
											{!active && (
												<DropdownMenuItem onClick={() => handleResend(m)}>
													{m.invitedAt
														? 'Resend invitation'
														: 'Send invitation'}
												</DropdownMenuItem>
											)}
											{!isPrimary && (
												<DropdownMenuItem
													onClick={() => handleMakePrimary(m)}
												>
													Make primary contact
												</DropdownMenuItem>
											)}
											{!isMe && (
												<>
													<DropdownMenuSeparator />
													<DropdownMenuItem
														variant="destructive"
														onClick={() => setRemoveTarget(m)}
													>
														Remove
													</DropdownMenuItem>
												</>
											)}
										</DropdownMenuContent>
									</DropdownMenu>
								</div>
							);
						})}
					</div>
				)}
			</div>

			{company && (
				<>
					<InviteMemberModal
						open={inviteOpen}
						onOpenChange={setInviteOpen}
						vetterEntityId={company.id}
						onSuccess={() => refetch()}
					/>
					<EditCompanyModal
						open={companyOpen}
						onOpenChange={setCompanyOpen}
						company={company}
						onSuccess={() => refetchProfile()}
					/>
				</>
			)}
			{me && (
				<EditProfileModal
					open={profileOpen}
					onOpenChange={setProfileOpen}
					member={me}
					onSuccess={() => {
						refetch();
						refetchProfile();
					}}
				/>
			)}
			<ConfirmDialog
				open={!!removeTarget}
				onOpenChange={(o) => !o && setRemoveTarget(null)}
				title="Remove from your team?"
				description={
					removeTarget
						? `${removeTarget.firstName} ${removeTarget.lastName} will no longer see your company's jobs.`
						: ''
				}
				confirmLabel="Remove"
				loading={removing}
				onConfirm={handleRemove}
			/>
		</div>
	);
}

function Initials({ name }: { name: string }) {
	const initials = name
		.split(' ')
		.map((n) => n[0])
		.slice(0, 2)
		.join('')
		.toUpperCase();
	return (
		<div className="flex size-8 shrink-0 items-center justify-center rounded-[var(--r-2)] border border-line-1 bg-surface-fill font-mono text-xs font-medium tracking-[0.04em] text-text-2">
			{initials}
		</div>
	);
}
